import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  FaGlobe,
  FaPlus,
  FaTrash,
  FaSave,
  FaUndo,
  FaUpload
} from 'react-icons/fa';
import { useToast } from '../../context/ToastContext';
import PageLayout from '../../components/ui/PageLayout';
import PageHeader from '../../components/ui/PageHeader';
import SkeletonLoader from '../../components/ui/SkeletonLoader';
import ServiceHeroExtrasEditor from '../../components/cms/ServiceHeroExtrasEditor';

const TaxReturnCMS = () => {
  const { success: toastSuccess, error: toastError } = useToast();
  const [activeTab, setActiveTab] = useState('hero');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    heroTitle: '',
    heroBody: '',
    heroImage: '',
    heroBtn1Text: '',
    heroBtn2Text: '',

    heroBadgeText: '',
    heroBadgeIcon: 'check',
    heroTrustBadges: [],

    servicesTitle: '',
    servicesSubtitle: '',
    services: [],

    trustTitle: '',
    trustSignals: [],

    stepsTitle: '',
    filingSteps: [],

    accuracyTitle: '',
    accuracySubtitle: '',
    accuracyBody: '',
    accuracyImage: '',

    faqsTitle: '',
    faqsSubtitle: '',
    faqs: [],

    isPublished: true
  });

  const fetchContent = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/tax-return/admin`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        const data = res.data.data || {};
        setForm({
          heroTitle: data.heroTitle || '',
          heroBody: data.heroBody || '',
          heroImage: data.heroImage || '',
          heroBtn1Text: data.heroBtn1Text || '',
          heroBtn2Text: data.heroBtn2Text || '',

          heroBadgeText: data.heroBadgeText || '',
          heroBadgeIcon: data.heroBadgeIcon || 'check',
          heroTrustBadges: data.heroTrustBadges || [],

          servicesTitle: data.servicesTitle || '',
          servicesSubtitle: data.servicesSubtitle || '',
          services: data.services || [],

          trustTitle: data.trustTitle || '',
          trustSignals: data.trustSignals || [],

          stepsTitle: data.stepsTitle || '',
          filingSteps: data.filingSteps || [],

          accuracyTitle: data.accuracyTitle || '',
          accuracySubtitle: data.accuracySubtitle || '',
          accuracyBody: data.accuracyBody || '',
          accuracyImage: data.accuracyImage || '',

          faqsTitle: data.faqsTitle || '',
          faqsSubtitle: data.faqsSubtitle || '',
          faqs: data.faqs || [],

          isPublished: data.isPublished !== false
        });
      }
    } catch (err) {
      toastError('Failed to load Tax Return content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/tax-return/admin`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toastSuccess('Tax Return page content saved successfully');
        fetchContent();
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const handleRestoreDefaults = async () => {
    if (!window.confirm('Are you sure you want to revert Tax Return page options to default?')) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/tax-return/admin/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toastSuccess('Restored default content successfully');
        fetchContent();
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to restore defaults');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);

    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/tax-return/admin/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`
          }
        }
      );
      if (res.data.success) {
        updateField(field, res.data.url);
        toastSuccess('Image uploaded and optimized successfully');
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to upload image');
    }
  };

  // List of Services helpers
  const addService = () => {
    setForm(prev => ({
      ...prev,
      services: [...prev.services, { title: '', description: '' }]
    }));
  };

  const removeService = (idx) => {
    setForm(prev => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== idx)
    }));
  };

  const updateServiceField = (idx, key, val) => {
    setForm(prev => {
      const list = [...prev.services];
      list[idx] = { ...list[idx], [key]: val };
      return { ...prev, services: list };
    });
  };

  // Trust signals helpers
  const addTrust = () => {
    setForm(prev => ({
      ...prev,
      trustSignals: [...prev.trustSignals, { heading: '', body: '' }]
    }));
  };

  const removeTrust = (idx) => {
    setForm(prev => ({
      ...prev,
      trustSignals: prev.trustSignals.filter((_, i) => i !== idx)
    }));
  };

  const updateTrustField = (idx, key, val) => {
    setForm(prev => {
      const list = [...prev.trustSignals];
      list[idx] = { ...list[idx], [key]: val };
      return { ...prev, trustSignals: list };
    });
  };

  // Filing Steps helpers
  const addStep = () => {
    setForm(prev => ({
      ...prev,
      filingSteps: [...prev.filingSteps, { title: '', description: '' }]
    }));
  };

  const removeStep = (idx) => {
    setForm(prev => ({
      ...prev,
      filingSteps: prev.filingSteps.filter((_, i) => i !== idx)
    }));
  };

  const updateStepField = (idx, key, val) => {
    setForm(prev => {
      const list = [...prev.filingSteps];
      list[idx] = { ...list[idx], [key]: val };
      return { ...prev, filingSteps: list };
    });
  };

  // FAQs helpers
  const addFAQ = () => {
    setForm(prev => ({
      ...prev,
      faqs: [...prev.faqs, { question: '', answer: '' }]
    }));
  };

  const removeFAQ = (idx) => {
    setForm(prev => ({
      ...prev,
      faqs: prev.faqs.filter((_, i) => i !== idx)
    }));
  };

  const updateFAQField = (idx, key, val) => {
    setForm(prev => {
      const list = [...prev.faqs];
      list[idx] = { ...list[idx], [key]: val };
      return { ...prev, faqs: list };
    });
  };

  if (loading) return <SkeletonLoader variant="page" />;

  return (
    <PageLayout>
      <PageHeader
        title="Tax Return Page CMS"
        description="Configure dynamic headings, description content, services cards, trust indicators, filing guides, and FAQs for the Tax Return page."
        icon={FaGlobe}
        actions={
          <div className="flex gap-3">
            <button
              onClick={handleRestoreDefaults}
              disabled={saving}
              className="px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-xl text-sm font-medium hover:bg-gray-50 dark:hover:bg-dark-700 flex items-center gap-2"
            >
              <FaUndo className="w-3.5 h-3.5" /> Revert Defaults
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-premium flex items-center gap-2"
            >
              <FaSave className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        }
      />

      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-dark-700 overflow-x-auto gap-2 mb-6">
        {[
          { id: 'hero', name: 'Hero Banner' },
          { id: 'services', name: 'Setup Services' },
          { id: 'trust', name: 'Trust Signals' },
          { id: 'steps', name: 'Filing Steps' },
          { id: 'accuracy', name: 'Accuracy Section' },
          { id: 'faqs', name: 'Page FAQs' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 font-bold text-sm border-b-2 whitespace-nowrap transition-all ${
              activeTab === tab.id
                ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-white'
            }`}
          >
            {tab.name}
          </button>
        ))}
      </div>

      <div className="space-y-6">
        {/* Tab 1: Hero Banner */}
        {activeTab === 'hero' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-5">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-dark-700 pb-4">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Hero Header Layout</h2>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.isPublished}
                  onChange={(e) => updateField('isPublished', e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                Published
              </label>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Hero Main Title</label>
              <input
                className="input-modern"
                value={form.heroTitle}
                onChange={(e) => updateField('heroTitle', e.target.value)}
                placeholder="Fast, Accurate and IRS-Compliant Tax Return Filing in the USA"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Hero Description Body</label>
              <textarea
                className="input-modern min-h-[100px]"
                value={form.heroBody}
                onChange={(e) => updateField('heroBody', e.target.value)}
                placeholder="Get your tax return services USA..."
              />
            </div>

            {/* Buttons */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 border-t border-b border-gray-100 dark:border-dark-700 py-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Primary Button Label</label>
                <input
                  className="input-modern text-sm"
                  value={form.heroBtn1Text}
                  onChange={(e) => updateField('heroBtn1Text', e.target.value)}
                  placeholder="File My Tax Return"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Secondary Button Label</label>
                <input
                  className="input-modern text-sm"
                  value={form.heroBtn2Text}
                  onChange={(e) => updateField('heroBtn2Text', e.target.value)}
                  placeholder="Talk to a Tax Expert"
                />
              </div>
            </div>

            <ServiceHeroExtrasEditor form={form} updateField={updateField} />

            {/* Image Upload */}
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Hero Side Image</label>
              <div className="flex items-center gap-4">
                <input
                  className="input-modern flex-1"
                  value={form.heroImage}
                  onChange={(e) => updateField('heroImage', e.target.value)}
                  placeholder="Image URL path..."
                />
                <label className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-xl text-sm font-medium hover:bg-gray-50 dark:hover:bg-dark-700 cursor-pointer whitespace-nowrap">
                  <FaUpload className="w-3.5 h-3.5" /> Upload Image
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'heroImage')}
                    className="hidden"
                  />
                </label>
              </div>
              {form.heroImage && (
                <div className="mt-2 relative w-48 h-32 rounded-xl overflow-hidden border border-gray-200">
                  <img
                    src={form.heroImage.startsWith('http') ? form.heroImage : `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000'}/${form.heroImage}`}
                    alt="Hero preview"
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab 2: Setup Services */}
        {activeTab === 'services' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-6">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-dark-700 pb-4">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Tax Return Services List</h2>
              <button
                onClick={addService}
                className="flex items-center gap-2 px-3 py-1.5 text-xs font-bold text-primary-600 bg-primary-50 dark:bg-primary-950/20 hover:bg-primary-100 rounded-xl transition-colors"
              >
                <FaPlus className="w-3.5 h-3.5" /> Add Service Card
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Section Header Title</label>
                <input
                  className="input-modern"
                  value={form.servicesTitle}
                  onChange={(e) => updateField('servicesTitle', e.target.value)}
                  placeholder="Our Complete Tax Return Filing Services in USA"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Section Subtitle</label>
                <input
                  className="input-modern"
                  value={form.servicesSubtitle}
                  onChange={(e) => updateField('servicesSubtitle', e.target.value)}
                  placeholder="Every filing type you need..."
                />
              </div>
            </div>

            {/* List of Services */}
            <div className="space-y-4 pt-4">
              {form.services.map((service, idx) => (
                <div key={idx} className="border border-gray-200 dark:border-dark-750 p-5 rounded-2xl relative space-y-3 bg-gray-50/20 dark:bg-dark-900/5">
                  <button
                    onClick={() => removeService(idx)}
                    className="absolute top-4 right-4 text-red-500 p-1.5 rounded-lg border border-red-200 hover:bg-red-50"
                  >
                    <FaTrash className="w-3 h-3" />
                  </button>

                  <h3 className="font-bold text-sm text-gray-800 dark:text-slate-200">Service Card #{idx + 1}</h3>

                  <div className="space-y-3">
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Title</label>
                      <input
                        className="input-modern text-sm py-1.5 px-3"
                        value={service.title}
                        onChange={(e) => updateServiceField(idx, 'title', e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Description</label>
                      <textarea
                        className="input-modern text-sm py-1.5 px-3 min-h-[70px]"
                        value={service.description}
                        onChange={(e) => updateServiceField(idx, 'description', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tab 3: Trust Signals */}
        {activeTab === 'trust' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-6">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-dark-700 pb-4">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Trust Indicators</h2>
              <button
                onClick={addTrust}
                className="flex items-center gap-2 px-3 py-1.5 text-xs font-bold text-primary-600 bg-primary-50 dark:bg-primary-950/20 hover:bg-primary-100 rounded-xl transition-colors"
              >
                <FaPlus className="w-3.5 h-3.5" /> Add Trust Signal
              </button>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Section Title</label>
              <input
                className="input-modern"
                value={form.trustTitle}
                onChange={(e) => updateField('trustTitle', e.target.value)}
                placeholder="Why Individuals and Businesses Trust BooksComply..."
              />
            </div>

            {/* List of Trust Signals */}
            <div className="space-y-4 pt-4">
              {form.trustSignals.map((trust, idx) => (
                <div key={idx} className="border border-gray-200 dark:border-dark-750 p-5 rounded-2xl relative space-y-3 bg-gray-50/20 dark:bg-dark-900/5">
                  <button
                    onClick={() => removeTrust(idx)}
                    className="absolute top-4 right-4 text-red-500 p-1.5 rounded-lg border border-red-200 hover:bg-red-50"
                  >
                    <FaTrash className="w-3 h-3" />
                  </button>

                  <h3 className="font-bold text-sm text-gray-800 dark:text-slate-200">Trust Signal #{idx + 1}</h3>

                  <div className="space-y-3">
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Heading</label>
                      <input
                        className="input-modern text-sm py-1.5 px-3"
                        value={trust.heading}
                        onChange={(e) => updateTrustField(idx, 'heading', e.target.value)}
                        placeholder="e.g. 🔹 IRS-Approved Experts"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Body Paragraph</label>
                      <textarea
                        className="input-modern text-sm py-1.5 px-3 min-h-[60px]"
                        value={trust.body}
                        onChange={(e) => updateTrustField(idx, 'body', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tab 4: Filing Steps */}
        {activeTab === 'steps' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-6">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-dark-700 pb-4">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Step-by-Step Filing Procedures</h2>
              <button
                onClick={addStep}
                className="flex items-center gap-2 px-3 py-1.5 text-xs font-bold text-primary-600 bg-primary-50 dark:bg-primary-950/20 hover:bg-primary-100 rounded-xl transition-colors"
              >
                <FaPlus className="w-3.5 h-3.5" /> Add Guide Step
              </button>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Section Title</label>
              <input
                className="input-modern"
                value={form.stepsTitle}
                onChange={(e) => updateField('stepsTitle', e.target.value)}
                placeholder="How to File a Tax Return in the USA — Step-by-Step Guide"
              />
            </div>

            {/* List of Steps */}
            <div className="space-y-4 pt-4">
              {form.filingSteps.map((step, idx) => (
                <div key={idx} className="border border-gray-200 dark:border-dark-750 p-5 rounded-2xl relative space-y-3 bg-gray-50/20 dark:bg-dark-900/5">
                  <button
                    onClick={() => removeStep(idx)}
                    className="absolute top-4 right-4 text-red-500 p-1.5 rounded-lg border border-red-200 hover:bg-red-50"
                  >
                    <FaTrash className="w-3 h-3" />
                  </button>

                  <h3 className="font-bold text-sm text-gray-800 dark:text-slate-200">Filing Step #{idx + 1}</h3>

                  <div className="space-y-3">
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Step Heading / Title</label>
                      <input
                        className="input-modern text-sm py-1.5 px-3"
                        value={step.title}
                        onChange={(e) => updateStepField(idx, 'title', e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Step Detail / Description</label>
                      <textarea
                        className="input-modern text-sm py-1.5 px-3 min-h-[60px]"
                        value={step.description}
                        onChange={(e) => updateStepField(idx, 'description', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tab 5: Accuracy Section */}
        {activeTab === 'accuracy' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-5">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white border-b border-gray-100 dark:border-dark-700 pb-3">Audit-Ready Accuracy Block</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Section Heading Title</label>
                <input
                  className="input-modern"
                  value={form.accuracyTitle}
                  onChange={(e) => updateField('accuracyTitle', e.target.value)}
                  placeholder="Accuracy with IRS-Compliant Tax Filing"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Section Subtitle / Eyebrow Text</label>
                <input
                  className="input-modern"
                  value={form.accuracySubtitle}
                  onChange={(e) => updateField('accuracySubtitle', e.target.value)}
                  placeholder="Audit-Ready Confidence"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Description Body Paragraph</label>
              <textarea
                className="input-modern min-h-[100px]"
                value={form.accuracyBody}
                onChange={(e) => updateField('accuracyBody', e.target.value)}
                placeholder="BooksComply follows a strict verification process..."
              />
            </div>

            {/* Accuracy Section Image */}
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Accuracy Banner Image</label>
              <div className="flex items-center gap-4">
                <input
                  className="input-modern flex-1"
                  value={form.accuracyImage}
                  onChange={(e) => updateField('accuracyImage', e.target.value)}
                  placeholder="Image URL path..."
                />
                <label className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-xl text-sm font-medium hover:bg-gray-50 dark:hover:bg-dark-700 cursor-pointer whitespace-nowrap">
                  <FaUpload className="w-3.5 h-3.5" /> Upload Image
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'accuracyImage')}
                    className="hidden"
                  />
                </label>
              </div>
              {form.accuracyImage && (
                <div className="mt-2 relative w-48 h-32 rounded-xl overflow-hidden border border-gray-200">
                  <img
                    src={form.accuracyImage.startsWith('http') ? form.accuracyImage : `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000'}/${form.accuracyImage}`}
                    alt="Accuracy preview"
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab 6: Page FAQs */}
        {activeTab === 'faqs' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-6">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-dark-700 pb-4">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Page FAQ Content</h2>
              <button
                onClick={addFAQ}
                className="flex items-center gap-2 px-3 py-1.5 text-xs font-bold text-primary-600 bg-primary-50 dark:bg-primary-950/20 hover:bg-primary-100 rounded-xl transition-colors"
              >
                <FaPlus className="w-3.5 h-3.5" /> Add FAQ Item
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">FAQ Grid Header Title</label>
                <input
                  className="input-modern"
                  value={form.faqsTitle}
                  onChange={(e) => updateField('faqsTitle', e.target.value)}
                  placeholder="Frequently Asked Questions about Tax Return Services..."
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">FAQ Grid Description</label>
                <input
                  className="input-modern"
                  value={form.faqsSubtitle}
                  onChange={(e) => updateField('faqsSubtitle', e.target.value)}
                  placeholder="Answers to the most common questions..."
                />
              </div>
            </div>

            {/* List of FAQs */}
            <div className="space-y-4 pt-4">
              {form.faqs.map((faq, idx) => (
                <div key={idx} className="border border-gray-200 dark:border-dark-750 p-5 rounded-2xl relative space-y-3 bg-gray-50/20 dark:bg-dark-900/5">
                  <button
                    onClick={() => removeFAQ(idx)}
                    className="absolute top-4 right-4 text-red-500 p-1.5 rounded-lg border border-red-200 hover:bg-red-50"
                  >
                    <FaTrash className="w-3 h-3" />
                  </button>

                  <h3 className="font-bold text-sm text-gray-800 dark:text-slate-200">FAQ Item #{idx + 1}</h3>

                  <div className="space-y-3">
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Question</label>
                      <input
                        className="input-modern text-sm py-1.5 px-3"
                        value={faq.question}
                        onChange={(e) => updateFAQField(idx, 'question', e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Answer</label>
                      <textarea
                        className="input-modern text-sm py-1.5 px-3 min-h-[80px]"
                        value={faq.answer}
                        onChange={(e) => updateFAQField(idx, 'answer', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default TaxReturnCMS;
